// ============================================================================
// Player headshots and team crests on the droplet.
//
// ONE definition, imported by every page that draws a player. There used to be
// eight copies of a hardcoded sport->folder map and they drifted apart: the
// tracker and EV listed five sports, Pick'em omitted NRL, and Prop EV listed
// only AFL and NRL — so NBA, WNBA and MLB player props drew no photo at all on
// Prop EV even though the files were sitting on the server.
//
// The folder is now DERIVED from the sport code rather than listed, exactly as
// team crests already were:
//
//     AFL  -> AFL_PLAYERS        NFL     -> NFL_PLAYERS
//     NRL  -> NRL_PLAYERS        LA_LIGA -> LALIGA_PLAYERS
//
// so ADDING A SPORT NEEDS NO CODE CHANGE. Upload
// /var/www/player-images/<SPORT>_PLAYERS/ and it works on every page at once.
//
// The trade-off, stated plainly: a sport with no folder yet costs one 404 per
// player row. That is what the old explicit lists were avoiding — except they
// did not, because both Prop EV and Pick'em fell back to a default folder
// (AFL / NBA) for an unknown sport and requested a photo that could not exist.
// Same 404, but pointed at the wrong league. A missing image fades out and the
// person placeholder takes its place, so the failure is invisible either way.
// ============================================================================

const ASSET_BASE = 'https://app.bookiecharity.com'

// Tours whose sport codes are per-TOURNAMENT rather than per-competition.
// config/sports.js lists 15 tennis ids (ATP_US_OPEN, ATP_WIMBLEDON,
// ATP_CHALLENGER, ...) and they are all the same circuit of players, so folding
// them to the tour keeps ONE folder instead of 15 identical ones.
const TOURS = ['ATP', 'WTA']

/** The code the asset folders are named after. */
const assetSport = (sport) => {
  const s = String(sport ?? '')
  return TOURS.find(t => s === t || s.startsWith(`${t}_`)) || s
}

/**
 * True where a "team" is really one person, so its picture is a headshot.
 *
 * Exported because it decides STYLING as well as the URL. A tennis head to head
 * or handicap arrives with team_name set, so anything keying off the FIELD
 * alone concludes "team" and draws the face bare like a crest, with no circular
 * frame. See subjectFor() in TRACKER_PAGE/LiveBetMeter.jsx.
 */
export const isIndividualSport = (sport) => TOURS.includes(assetSport(sport))

/**
 * Sport code -> asset folder. Internal underscores are STRIPPED, because the
 * folders on the droplet are LALIGA_LOGOS / SERIEA_LOGOS / LIGUE1_LOGOS /
 * ALEAGUE_LOGOS while the sport codes in config/sports.js are LA_LIGA /
 * SERIE_A / LIGUE_1 / A_LEAGUE. Single-word codes are unaffected.
 */
export const playerImageFolder = (sport) =>
  sport ? `${assetSport(sport).replace(/_/g, '')}_PLAYERS` : null

export const teamLogoFolder = (sport) =>
  sport ? `${assetSport(sport).replace(/_/g, '')}_LOGOS` : null

/**
 * Name -> filename: fold accents, drop periods and ampersands, close the gaps,
 * LOWERCASE.
 *
 * PERIODS AND AMPERSANDS ONLY. Verified against the droplet, which keeps every
 * other mark:
 *   "St. Louis Cardinals"     -> stlouiscardinals        (period dropped)
 *   "D.C. United"             -> dcunited                (period dropped)
 *   "Brighton & Hove Albion"  -> brightonhovealbion      (ampersand dropped)
 *   "De'Aaron Fox"            -> de'aaronfox             (apostrophe kept)
 *   "Shai Gilgeous-Alexander" -> shaigilgeous-alexander  (hyphen kept)
 *
 * THOSE TWO CHARACTERS ONLY. Do not "tidy" this into a strip-everything rule:
 * that breaks the apostrophe and hyphen names, which work today.
 *
 * LOWERCASE because the match happens on a case-sensitive Linux filesystem, not
 * in this file - nginx either finds the exact filename or 404s, so there is no
 * comparison here to loosen. Folding both sides to one case is the only way a
 * feed that shouts "JANNIK SINNER" still finds its photo. Lowercase rather than
 * title case because ~400 files carry a legitimate inner capital (BalynO'Brien,
 * AlexNeal-Bullen, AndrewMcGrath) that no re-capitalisation rule reproduces.
 *
 * Dropping the periods HERE while the droplet kept them is what left 45 files
 * unreachable for months (MLB_PLAYERS/A.J.Ewing.webp against a request for
 * AJEwing.webp). The droplet was renamed to match, by the same transform this
 * applies, with a hardlink per file so cached capitalised URLs still resolve.
 *
 * AMPERSANDS ARE DROPPED TOO, verified the same way against the droplet:
 *   "Brighton & Hove Albion" -> brightonhovealbion
 * It is the only name in any feed carrying one, and the asset has never had it,
 * so a request that kept the & 404'd and that crest never drew. This stays an
 * EXPLICIT character list - period and ampersand - NOT a strip-all: apostrophes
 * and hyphens are real parts of filenames on disk (de'aaronfox,
 * shaigilgeous-alexander) and removing them breaks names that work today.
 */
export const toAssetFile = (name) =>
  String(name)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[.&]/g, '')
    .trim()
    .split(/\s+/)
    .join('')
    .toLowerCase()

/** Headshot URL from a player's NAME. */
export function playerImageUrl(playerName, sport) {
  const folder = playerImageFolder(sport)
  if (!folder || !playerName) return null
  return `${ASSET_BASE}/player-images/${folder}/${toAssetFile(playerName)}.webp`
}

/**
 * Headshot URL from a SELECTION string ("Marcus Bontempelli Over 20.5").
 *
 * Prop EV and Pick'em render off the display string rather than a player_name
 * field, so the name has to be cut out of it first.
 */
export function playerImageUrlFromSelection(selection, sport) {
  const name = String(selection ?? '').split(/ over | under /i)[0].trim()
  return playerImageUrl(name, sport)
}

/**
 * Crest URL for a team. Names are already standardised, so they map directly.
 *
 * In tennis the two "teams" ARE the two players — a fixture is "Ben Shelton vs
 * Stefanos Tsitsipas" — so their picture is a headshot from the tour's player
 * folder, not a crest. Without this, every tennis fixture asks for a crest that
 * cannot exist.
 */
export function teamLogoUrl(teamName, sport) {
  if (isIndividualSport(sport)) return playerImageUrl(teamName, sport)
  const folder = teamLogoFolder(sport)
  if (!folder || !teamName) return null
  return `${ASSET_BASE}/team-logos/${folder}/${toAssetFile(teamName)}.webp`
}
